/**
 * ventilacao-script.js
 * Calculadora de Ventilação Natural Residencial (modelo educativo)
 *
 * Estima renovações de ar por hora (ACH) pelo efeito vento (Q = Cv·A·V) e
 * compara a área de janelas com a regra típica de 1/8 do piso de códigos de
 * obras. Referências: ASHRAE Fundamentals, BS 5925/CIBSE, ASHRAE 62.1.
 */

import { App } from '../src/core/app.js';
import { i18n } from '../src/core/i18n.js';
import { formatarNumero } from '../src/utils/formatters.js';
import { ExplicacaoResultado } from '../src/components/resultado-explicado.js';
import {
    VENTILACAO_SE,
    RECUPERACAO_CALOR_SE,
    GRADDAGAR_SE,
    AR_RHO_CP
} from '../src/data/parametros-suecia.js';

// ============================================
// CONSTANTES
// ============================================

const SLIDER_TO_INPUT = {
    sliderComprimento: 'inputComprimento',
    sliderLargura:     'inputLargura',
    sliderPeDireito:   'inputPeDireito',
    sliderJanelas:     'inputJanelas',
    sliderPessoas:     'inputPessoas'
};

// Velocidade típica do vento por clima (m/s)
const VEL_VENTO = {
    quente:    2.5,
    temperado: 2.0,
    frio:      1.5
};

// Exposição da fachada das janelas ao vento dominante LOCAL. Não existe
// orientação cardeal universalmente favorável (os ventos dominantes variam
// por região); o que importa é a janela estar a favor do vento.
const FATOR_EXPOSICAO = {
    favoravel: 1.00,   // fachada a favor do vento (vento ~perpendicular)
    obliqua:   0.85,   // vento oblíquo à fachada
    abrigada:  0.70    // fachada abrigada/sotavento
};

// Coeficiente efetivo de vazão por tipo de ventilação (só efeito vento,
// janelas totalmente abertas; efeito chaminé não modelado):
// - cruzada: Q = Cv·A·V com Cv ≈ 0,60 (ASHRAE Fundamentals, vento
//   perpendicular; A = área de abertura da fachada de ENTRADA, a menor);
// - unilateral (janelas numa só fachada — caso residencial típico):
//   Q ≈ 0,025·A·V (BS 5925 / CIBSE AM10).
const COEF_VENTILACAO = {
    unilateral: 0.025,
    cruzada:    0.60
};

// Alvo educativo de qualidade do ar interior (recomendações sanitárias de
// renovação, ex. CDC ≥5 ACH). NÃO é exigência da NBR 15575 (~1 ren/h de
// referência) nem da ASHRAE 62.2 (fórmula por área + ocupantes).
const ACH_ALVO = 6;

// Vazão de ar fresco por pessoa (m³/h) — ASHRAE 62.1
const VAZAO_POR_PESSOA = 27; // 7,5 L/s × 3,6

// Referência educativa de área ventilável mínima: regra típica de código de
// obras municipal (1/8 do piso). A NBR 15575-4 define percentuais por zona
// bioclimática (tipicamente 7–12%) — verificar a exigência local.
const FRACAO_AREA_MINIMA = 1 / 8; // 12,5%

// ============================================
// SUÉCIA — vazão exigida e perda de calor
// ============================================

/**
 * A pergunta sueca é outra. No Brasil e na Itália pergunta-se se a janela é
 * grande o bastante; na Suécia a exigência é de VAZÃO de ar exterior o ano
 * inteiro (BFS 2024:8, 3 kap. 5 §), e o problema de projeto passa a ser a
 * perda de calor que essa vazão obrigatória carrega para fora.
 *
 * Função pura: recebe valores, devolve números. Sem DOM.
 */
function calcularVentilacaoSuecia(v) {
    const areaPiso = v.comprimento * v.largura;
    const volume   = areaPiso * v.peDireito;

    // Os dois critérios do regulamento; vale o MAIOR.
    const fluxoPorArea    = VENTILACAO_SE.fluxoPorM2 * areaPiso;      // l/s
    const fluxoPorPessoas = VENTILACAO_SE.fluxoPorPessoa * v.pessoas; // l/s
    const fluxoExigido    = Math.max(fluxoPorArea, fluxoPorPessoas);
    const criterio        = fluxoPorArea >= fluxoPorPessoas ? 'area' : 'pessoas';

    const fluxoM3h = fluxoExigido * 3.6;
    const omsattning = volume > 0 ? fluxoM3h / volume : 0;

    // Perda de calor anual: E = V̇ · ρcp · graus-dia.
    // V̇ em m³/s, graus-dia em K·dia → segundos por dia e J→kWh.
    const graddagar   = GRADDAGAR_SE[v.landsdel] ?? GRADDAGAR_SE.mellan;
    const fluxoM3s    = fluxoExigido / 1000;
    const perdaBruta  = (fluxoM3s * AR_RHO_CP * graddagar * 86400) / 3.6e6; // kWh/ano

    const recuperacao = RECUPERACAO_CALOR_SE[v.sistema] ?? 0;
    const recuperado  = perdaBruta * recuperacao;
    const perdaLiquida = perdaBruta - recuperado;
    const custoAnual   = perdaLiquida * v.energipris;

    // Quanto se ganharia trocando o sistema atual por FTX.
    const perdaComFtx   = perdaBruta * (1 - RECUPERACAO_CALOR_SE.ftx);
    const economiaFtx   = (perdaLiquida - perdaComFtx) * v.energipris;

    return {
        areaPiso, volume, fluxoPorArea, fluxoPorPessoas, fluxoExigido, criterio,
        fluxoM3h, omsattning, graddagar, perdaBruta, recuperacao, recuperado,
        perdaLiquida, custoAnual, economiaFtx
    };
}

/** Número no formato sueco (espaço como separador de milhar). */
function numSE(valor, casas = 0) {
    return valor.toLocaleString('sv-SE', {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas
    });
}

// ============================================
// CLASSE PRINCIPAL
// ============================================

class VentilacaoApp extends App {
    constructor() {
        super({
            appName: 'ventilacao',
            callbacks: {
                aoInicializar: () => this.inicializarVentilacao(),
                aoTrocarIdioma: () => this.atualizarResultado()
            }
        });
        this.explicacao = new ExplicacaoResultado('v2-explicacao', i18n);
    }

    get traducoes() {
        const idioma = i18n.obterIdiomaAtual();
        return this.config.traducoes[idioma] || this.config.traducoes['pt-BR'] || {};
    }

    inicializarVentilacao() {
        this.configurarEventos();
        document.addEventListener('engnata:themechange', () => this.atualizarResultado());
        this.atualizarResultado();
    }

    // ============================================
    // CONFIGURAÇÃO DE EVENTOS
    // ============================================

    configurarEventos() {
        this.configurarIconesInfo();
        this.configurarSlidersEInputs();
        this.configurarEventosSE();

        document.querySelectorAll('input[name="tipoVentilacao"]').forEach(r =>
            r.addEventListener('change', () => this.atualizarResultado()));
        document.querySelectorAll('input[name="exposicao"]').forEach(r =>
            r.addEventListener('change', () => this.atualizarResultado()));
        document.querySelectorAll('input[name="clima"]').forEach(r =>
            r.addEventListener('change', () => this.atualizarResultado()));

        // Memorial
        document.getElementById('btnMemorial')?.addEventListener('click', () => this.toggleMemorial());
        document.getElementById('btnFecharMemorial')?.addEventListener('click', () => this.toggleMemorial());
        document.querySelectorAll('.btn-voltar-memorial').forEach(btn =>
            btn.addEventListener('click', () => this.toggleMemorial()));
    }

    toggleMemorial() {
        const memorial   = document.getElementById('memorialSection');
        const resultados = document.getElementById('resultadosSection');
        if (!memorial || !resultados) return;
        const mostrar = memorial.style.display === 'none';
        memorial.style.display   = mostrar ? 'block' : 'none';
        resultados.style.display = mostrar ? 'none'  : 'block';
        if (mostrar) memorial.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    atualizarMemorial(v, res) {
        const t = this.traducoes;
        const set = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
        const achPessoas = (v.pessoas * VAZAO_POR_PESSOA) / res.volume;

        set('memorial-exemplo-volume',
            `${v.comprimento} × ${v.largura} = ${formatarNumero(res.areaPiso, 1)} m² → ${formatarNumero(res.areaPiso, 1)} × ${v.peDireito} = ${formatarNumero(res.volume, 1)} m³`);
        set('memorial-exemplo-vazao',
            `${formatarNumero(v.janelas, 1)} × ${formatarNumero(res.velVento, 1)} × ${formatarNumero(res.fatorExp, 2)} × ${formatarNumero(res.coef, 3)} × 3600 = ${formatarNumero(res.vazao, 0)} m³/h`);
        set('memorial-exemplo-ach',
            `${formatarNumero(res.vazao, 0)} ÷ ${formatarNumero(res.volume, 1)} = ${formatarNumero(res.ach, 1)} ACH`);
        set('memorial-exemplo-achmin',
            `max(${ACH_ALVO} ; ${v.pessoas} × 27 ÷ ${formatarNumero(res.volume, 1)} = ${formatarNumero(achPessoas, 1)}) = ${formatarNumero(res.achNecessario, 1)} ACH`);
        set('memorial-exemplo-areamin',
            `${formatarNumero(res.areaPiso, 1)} ÷ 8 = ${formatarNumero(res.areaMinima, 2)} m²`);

        set('resumo-dims',     `${v.comprimento} × ${v.largura} m`);
        set('resumo-pd',       `${v.peDireito} m`);
        set('resumo-janelas',  `${v.janelas} m²`);
        set('resumo-pessoas',  `${v.pessoas}`);
        set('resumo-tipo',     t?.opcoes?.[v.tipoVentilacao] ?? v.tipoVentilacao);
        set('resumo-ori',      t?.opcoes?.[v.exposicao] ?? v.exposicao);
        set('resumo-clima',    t?.opcoes?.[v.clima] ?? v.clima);
        set('resumo-volume',   `${formatarNumero(res.volume, 1)} m³`);
        set('resumo-vazao',    `${formatarNumero(res.vazao, 0)} m³/h`);
        set('resumo-ach',      `${formatarNumero(res.ach, 1)} ACH`);
        set('resumo-achmin',   `${formatarNumero(res.achNecessario, 1)} ACH`);
        set('resumo-areamin',  `${formatarNumero(res.areaMinima, 2)} m²`);
        set('resumo-qualidade', t?.qualidade?.[res.qualidadeKey] ?? res.qualidadeKey);
    }

    configurarIconesInfo() {
        [
            { iconId: 'infoIconComprimento', descricaoId: 'descricaoComprimento' },
            { iconId: 'infoIconLargura',     descricaoId: 'descricaoLargura'     },
            { iconId: 'infoIconPeDireito',   descricaoId: 'descricaoPeDireito'   },
            { iconId: 'infoIconJanelas',     descricaoId: 'descricaoJanelas'     },
            { iconId: 'infoIconPessoas',     descricaoId: 'descricaoPessoas'     }
        ].forEach(({ iconId, descricaoId }) => {
            const icon = document.getElementById(iconId);
            const desc = document.getElementById(descricaoId);
            if (!icon || !desc) return;
            const toggle = () => {
                const aberto = desc.style.display === 'none';
                desc.style.display = aberto ? 'block' : 'none';
                icon.setAttribute('aria-expanded', String(aberto));
            };
            icon.setAttribute('aria-expanded', 'false');
            icon.addEventListener('click', toggle);
            icon.addEventListener('keydown', e => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
            });
        });
    }

    configurarSlidersEInputs() {
        Object.entries(SLIDER_TO_INPUT).forEach(([sliderId, inputId]) => {
            const slider = document.getElementById(sliderId);
            const input  = document.getElementById(inputId);
            if (!slider || !input) return;

            slider.addEventListener('input', () => {
                input.value = parseFloat(slider.value).toFixed(this.decimaisPorSlider(slider));
                this.atualizarResultado();
            });

            input.addEventListener('change', () => {
                const valor = parseFloat(input.value.replace(',', '.'));
                if (!isNaN(valor)) {
                    slider.value = valor;
                }
                // Entrada inválida ou clampada: o display volta ao valor do
                // slider e o estado visual de fora-de-faixa é limpo (o clamp
                // programático não dispara 'input' — ver src/core/app.js).
                input.value = parseFloat(slider.value).toFixed(this.decimaisPorSlider(slider));
                slider.classList.remove('slider-fora-faixa');
                this.atualizarResultado();
            });
        });
    }

    decimaisPorSlider(slider) {
        const step = parseFloat(slider.step) || 1;
        if (step < 1) {
            return String(step).split('.')[1]?.length || 1;
        }
        return 0;
    }

    // ============================================
    // CÁLCULO
    // ============================================

    obterValores() {
        return {
            comprimento: parseFloat(document.getElementById('inputComprimento').value) || 5,
            largura:     parseFloat(document.getElementById('inputLargura').value)     || 4,
            peDireito:   parseFloat(document.getElementById('inputPeDireito').value)   || 2.7,
            janelas:     parseFloat(document.getElementById('inputJanelas').value)     || 2,
            pessoas:     parseInt(document.getElementById('inputPessoas').value)       || 2,
            tipoVentilacao: document.querySelector('input[name="tipoVentilacao"]:checked')?.value || 'unilateral',
            exposicao:   document.querySelector('input[name="exposicao"]:checked')?.value || 'favoravel',
            clima:       document.querySelector('input[name="clima"]:checked')?.value  || 'quente'
        };
    }

    calcular(v) {
        const areaPiso  = v.comprimento * v.largura;           // m²
        const volume    = areaPiso * v.peDireito;              // m³
        const areaMinima = areaPiso * FRACAO_AREA_MINIMA;      // regra típica 1/8

        const velVento  = VEL_VENTO[v.clima]              || 2.0;
        const fatorExp  = FATOR_EXPOSICAO[v.exposicao]    || 0.85;
        const coef      = COEF_VENTILACAO[v.tipoVentilacao] || COEF_VENTILACAO.unilateral;

        // Vazão por efeito vento: Q = Cv × A × V (× fator de exposição)
        const vazao = v.janelas * velVento * fatorExp * coef * 3600; // m³/h

        // ACH real
        const ach = vazao / volume;

        // Alvo: maior entre o alvo educativo de qualidade do ar e o necessário por pessoa
        const achPorPessoas = (v.pessoas * VAZAO_POR_PESSOA) / volume;
        const achNecessario = Math.max(ACH_ALVO, achPorPessoas);

        // Qualidade
        let qualidadeKey;
        if (ach >= achNecessario * 1.5) {
            qualidadeKey = 'excelente';
        } else if (ach >= achNecessario) {
            qualidadeKey = 'boa';
        } else if (ach >= achNecessario * 0.5) {
            qualidadeKey = 'aceitavel';
        } else {
            qualidadeKey = 'insuficiente';
        }

        // Déficit/excedente de janelas
        const deficitJanelas = areaMinima - v.janelas; // positivo = falta, negativo = sobra

        return { areaPiso, volume, areaMinima, velVento, fatorExp, coef, vazao, ach, achNecessario, qualidadeKey, deficitJanelas };
    }

    // ============================================
    // DOM
    // ============================================

    atualizarResultado() {
        const v   = this.obterValores();
        const res = this.calcular(v);

        // `body.lang-se` comanda as regras .se-only / .se-hide do CSS.
        const ehSueco = i18n.obterIdiomaAtual() === 'sv-SE';
        document.body.classList.toggle('lang-se', ehSueco);
        document.body.classList.toggle('lang-br', i18n.obterIdiomaAtual() === 'pt-BR');
        document.body.classList.toggle('lang-it', i18n.obterIdiomaAtual() === 'it-IT');

        const resSE = this.atualizarSuecia(v);

        this.atualizarDOM(v, res);
        this.atualizarMemorial(v, res);
        this.explicacao.renderizar(
            ehSueco && resSE ? this.gerarExplicacaoSE(v, resSE) : this.gerarExplicacao(v, res)
        );
    }

    atualizarDOM(v, res) {
        const t    = this.traducoes;
        // ACH
        document.getElementById('resultadoACH').textContent =
            formatarNumero(res.ach, 1) + ' ACH';

        // Qualidade — badge com cor semafórica
        const qualLabel = t.qualidade?.[res.qualidadeKey] || res.qualidadeKey;
        const achEl = document.getElementById('resultadoQualidade');
        achEl.innerHTML = `<span class="qualidade-badge qualidade-${res.qualidadeKey}">${qualLabel}</span>`;

        // Volume
        document.getElementById('resultadoVolume').textContent =
            formatarNumero(res.volume, 1) + ' m³';

        // Vazão
        document.getElementById('resultadoVazao').textContent =
            formatarNumero(res.vazao, 0) + ' m³/h';

        // Área mínima (regra típica de 1/8 do piso)
        document.getElementById('resultadoAreaMinima').textContent =
            formatarNumero(res.areaMinima, 2) + ' m²  (1/8 × ' + formatarNumero(res.areaPiso, 1) + ' m²)';

        // Status das janelas
        const statusEl = document.getElementById('resultadoStatusJanelas');
        if (res.deficitJanelas > 0) {
            const falta = i18n.porIdioma({ 'pt-BR': 'Faltam', 'it-IT': 'Mancano', 'sv-SE': 'Saknas' });
            statusEl.innerHTML = `<span class="status-badge status-falta">⚠️ ${falta} ${formatarNumero(res.deficitJanelas, 2)} m²</span>`;
        } else {
            const ok = i18n.porIdioma({ 'pt-BR': 'OK — excedente', 'it-IT': 'OK — in eccesso', 'sv-SE': 'OK — överskott' });
            statusEl.innerHTML = `<span class="status-badge status-ok">✅ ${ok} ${formatarNumero(-res.deficitJanelas, 2)} m²</span>`;
        }
    }

    // ============================================
    // SUÉCIA
    // ============================================

    obterValoresSE() {
        const bruto = document.getElementById('inputSeEnergipris')?.value || '1,20';
        const energipris = parseFloat(String(bruto).replace(/\s/g, '').replace(',', '.')) || 1.2;

        return {
            sistema:  document.getElementById('selectSeSistema')?.value   || 'ftx',
            landsdel: document.getElementById('selectSeLandsdel')?.value  || 'mellan',
            energipris
        };
    }

    atualizarSuecia(v) {
        if (!document.getElementById('seFluxoExigido')) return;

        const se  = this.obterValoresSE();
        const r   = calcularVentilacaoSuecia({ ...v, ...se });
        const t   = this.traducoes;
        const def = (id, texto) => {
            const el = document.getElementById(id);
            if (el) el.textContent = texto;
        };

        def('seFluxoExigido', `${numSE(r.fluxoExigido, 1)} l/s`);

        // Qual dos dois critérios manda é informação útil: numa casa grande com
        // poucos moradores manda a área; num quarto pequeno e cheio, as pessoas.
        const criterioTexto = r.criterio === 'area'
            ? (t.vense?.criterioArea || '0,35 l/s·m² (golvarea)')
            : (t.vense?.criterioPessoas || '4,0 l/s per person');
        def('seCriterio', criterioTexto);

        def('seFluxoM3h', `${numSE(r.fluxoM3h, 0)} m³/h`);
        def('seOmsattning', `${numSE(r.omsattning, 2)} oms/h`);
        def('seForlustBrutto', `${numSE(r.perdaBruta, 0)} kWh/år`);
        def('seAtervunnet', `${numSE(r.recuperado, 0)} kWh/år (${numSE(r.recuperacao * 100, 0)} %)`);
        def('seNetto', `${numSE(r.perdaLiquida, 0)} kWh/år`);
        def('seKostnad', `${numSE(r.custoAnual, 0)} kr/år`);

        // A comparação com FTX só faz sentido para quem ainda não tem FTX.
        const itemJamforelse = document.getElementById('seJamforelseItem');
        if (itemJamforelse) {
            const ehFtx = se.sistema === 'ftx';
            itemJamforelse.hidden = ehFtx;
            if (!ehFtx) {
                def('seJamforelse', `−${numSE(r.economiaFtx, 0)} kr/år`);
            }
        }

        return r;
    }

    configurarEventosSE() {
        const recalcular = () => this.atualizarResultado();

        document.getElementById('selectSeSistema')?.addEventListener('change', recalcular);
        document.getElementById('selectSeLandsdel')?.addEventListener('change', recalcular);

        const input  = document.getElementById('inputSeEnergipris');
        const slider = document.getElementById('sliderSeEnergipris');

        slider?.addEventListener('input', () => {
            if (input) input.value = numSE(parseFloat(slider.value), 2);
            recalcular();
        });

        input?.addEventListener('input', () => {
            const n = parseFloat(String(input.value).replace(/\s/g, '').replace(',', '.'));
            if (slider && Number.isFinite(n)) slider.value = String(n);
            recalcular();
        });
    }

    /**
     * Explicação da visão sueca. Não é a tradução da explicação brasileira: as
     * linhas são outras porque o cálculo é outro.
     */
    gerarExplicacaoSE(v, r) {
        const t = this.traducoes;
        const nomeSistema = t.vense?.[{
            sjalvdrag: 'sistemaSjalvdrag',
            franluft:  'sistemaFranluft',
            fvp:       'sistemaFvp',
            ftx:       'sistemaFtx'
        }[this.obterValoresSE().sistema]] || '';

        const mandaArea = r.criterio === 'area';

        const linhas = [
            {
                icone: '📏',
                titulo: 'Krav på uteluftsflöde',
                valor: `${numSE(r.fluxoExigido, 1)} l/s`,
                descricao: `Golvarea ${numSE(r.areaPiso, 1)} m² × 0,35 = ${numSE(r.fluxoPorArea, 1)} l/s. ` +
                    `${v.pessoas} personer × 4,0 = ${numSE(r.fluxoPorPessoas, 1)} l/s. ` +
                    `Det högre värdet gäller — här ${mandaArea ? 'golvarean' : 'antalet personer'}.`
            },
            {
                icone: '🔄',
                titulo: 'Luftomsättning',
                valor: `${numSE(r.omsattning, 2)} oms/h`,
                descricao: `${numSE(r.fluxoM3h, 0)} m³/h ÷ ${numSE(r.volume, 1)} m³. ` +
                    'Minimikravet motsvarar ungefär en halv omsättning i timmen — tumregeln 0,5 oms/h.'
            },
            {
                icone: '🌡️',
                titulo: 'Ventilationsförlust',
                valor: `${numSE(r.perdaBruta, 0)} kWh/år`,
                descricao: `Flödet ${numSE(r.fluxoExigido, 1)} l/s × luftens värmekapacitet × ` +
                    `${numSE(r.graddagar, 0)} graddagar (normalår). Detta är värmen som ` +
                    'ventilationen för ut ur huset, innan återvinning.'
            },
            {
                icone: '♻️',
                titulo: 'Återvinning',
                valor: `${numSE(r.recuperado, 0)} kWh/år`,
                descricao: `${nomeSistema}: ${numSE(r.recuperacao * 100, 0)} % återvinns, ` +
                    `${numSE(r.perdaLiquida, 0)} kWh/år måste värmas. ` +
                    `Till ${numSE(this.obterValoresSE().energipris, 2)} kr/kWh blir det ` +
                    `${numSE(r.custoAnual, 0)} kr per år.`
            }
        ];

        const destaque = r.economiaFtx > 1
            ? `Ett FTX-aggregat skulle spara ${numSE(r.economiaFtx, 0)} kr per år`
            : `${numSE(r.fluxoExigido, 1)} l/s krävs — ${numSE(r.custoAnual, 0)} kr/år att värma`;

        return {
            linhas,
            destaque,
            dica: 'Flödet får sänkas till 0,10 l/s per m² när ingen är hemma, men aldrig ' +
                'under 0,35 l/s per m² när någon vistas i bostaden. Villor och radhus ' +
                'behöver OVK endast vid nybyggnation; flerbostadshus vart 3:e år med FTX.',
            norma: 'BFS 2024:8, 3 kap. Luft, 5 § (ersatte BBR den 1 juli 2026) · ' +
                'graddagar enligt SMHI normalår 1991–2020 · pedagogisk beräkning'
        };
    }

    gerarExplicacao(v, res) {
        const t    = this.traducoes;
        const t2 = mapa => i18n.porIdioma(mapa);

        const nomeClima     = t.opcoes?.[v.clima]          || v.clima;
        const nomeExposicao = t.opcoes?.[v.exposicao]      || v.exposicao;
        const nomeTipo      = t.opcoes?.[v.tipoVentilacao] || v.tipoVentilacao;
        const qualLabel     = t.qualidade?.[res.qualidadeKey] || res.qualidadeKey;
        const achPessoas    = formatarNumero((v.pessoas * VAZAO_POR_PESSOA) / res.volume, 1);

        return {
            linhas: [
                {
                    icone: '📐',
                    titulo: t2({ 'pt-BR': 'Volume do ambiente', 'it-IT': 'Volume dell\'ambiente', 'sv-SE': 'Rummets volym' }),
                    valor: `${formatarNumero(res.volume, 1)} m³`,
                    descricao: `${v.comprimento} m × ${v.largura} m × ${v.peDireito} m = ${formatarNumero(res.volume, 1)} m³`
                },
                {
                    icone: '💨',
                    titulo: t2({ 'pt-BR': 'Vazão de ar', 'it-IT': 'Portata d\'aria', 'sv-SE': 'Luftflöde' }),
                    valor: `${formatarNumero(res.vazao, 0)} m³/h`,
                    descricao: t2({
                        'pt-BR': `Vento ${nomeClima} (${formatarNumero(res.velVento, 1)} m/s), ${nomeTipo} (Cv=${formatarNumero(res.coef, 3)}), exposição ${nomeExposicao} (fator ${formatarNumero(res.fatorExp, 2)}). Fórmula: A × V × fator × Cv × 3600.`,
                        'it-IT': `Vento ${nomeClima} (${formatarNumero(res.velVento, 1)} m/s), ${nomeTipo} (Cv=${formatarNumero(res.coef, 3)}), esposizione ${nomeExposicao} (fattore ${formatarNumero(res.fatorExp, 2)}). Formula: A × V × fattore × Cv × 3600.`,
                        'sv-SE': `Vind ${nomeClima} (${formatarNumero(res.velVento, 1)} m/s), ${nomeTipo} (Cv=${formatarNumero(res.coef, 3)}), läge ${nomeExposicao} (faktor ${formatarNumero(res.fatorExp, 2)}). Formel: A × V × faktor × Cv × 3600.`
                    })
                },
                {
                    icone: '🔄',
                    titulo: t2({ 'pt-BR': 'Renovações de ar (ACH)', 'it-IT': 'Ricambi d\'aria (ACH)', 'sv-SE': 'Luftomsättning (ACH)' }),
                    valor: `${formatarNumero(res.ach, 1)} ACH`,
                    descricao: t2({
                        'pt-BR': `Alvo: ${formatarNumero(res.achNecessario, 1)} ACH (maior entre o alvo educativo ${ACH_ALVO} e o necessário por pessoas ${achPessoas}).`,
                        'it-IT': `Obiettivo: ${formatarNumero(res.achNecessario, 1)} ACH (maggiore tra l'obiettivo educativo ${ACH_ALVO} e il fabbisogno per persone ${achPessoas}).`,
                        'sv-SE': `Mål: ${formatarNumero(res.achNecessario, 1)} ACH (det högsta av det pedagogiska målet ${ACH_ALVO} och behovet för antalet personer ${achPessoas}).`
                    })
                },
                {
                    icone: '🪟',
                    titulo: t2({ 'pt-BR': 'Área mínima de janelas (referência 1/8)', 'it-IT': 'Superficie minima finestre (riferimento 1/8)', 'sv-SE': 'Minsta fönsterarea (riktvärde 1/8)' }),
                    valor: `${formatarNumero(res.areaMinima, 2)} m²`,
                    descricao: t2({
                        'pt-BR': `1/8 da área do piso (${formatarNumero(res.areaPiso, 1)} m²) — regra típica de código de obras. Atual: ${v.janelas} m² — ${res.deficitJanelas > 0 ? 'insuficiente' : 'conforme'}. Critério construtivo independente do ACH.`,
                        'it-IT': `1/8 dell'area del pavimento (${formatarNumero(res.areaPiso, 1)} m²) — regola tipica dei regolamenti edilizi. Attuale: ${v.janelas} m² — ${res.deficitJanelas > 0 ? 'insufficiente' : 'conforme'}. Criterio costruttivo indipendente dall'ACH.`,
                        'sv-SE': `1/8 av golvytan (${formatarNumero(res.areaPiso, 1)} m²) — vanlig byggregel. Nuvarande: ${v.janelas} m² — ${res.deficitJanelas > 0 ? 'otillräckligt' : 'uppfyllt'}. Byggnadskrav oberoende av ACH; i Sverige gäller i stället 0,35 l/s per m² enligt BFS 2024:8.`
                    })
                }
            ],
            destaque: t2({
                'pt-BR': `Qualidade da ventilação: ${qualLabel} — ${formatarNumero(res.ach, 1)} ACH`,
                'it-IT': `Qualità ventilazione: ${qualLabel} — ${formatarNumero(res.ach, 1)} ACH`,
                'sv-SE': `Ventilationens kvalitet: ${qualLabel} — ${formatarNumero(res.ach, 1)} ACH`
            }),
            dica: t2({
                'pt-BR': 'Com janelas em fachadas opostas, selecione o tipo "cruzada": a vazão estimada pode ser uma ordem de grandeza maior que a unilateral.',
                'it-IT': 'Con finestre su facciate opposte, seleziona il tipo "incrociata": la portata stimata può essere di un ordine di grandezza superiore a quella unilaterale.',
                'sv-SE': 'Med fönster på motstående fasader, välj typen "korsdrag": det uppskattade flödet kan bli en tiopotens större än vid ensidig ventilation.'
            }),
            norma: t2({
                'pt-BR': 'Modelo educativo — ASHRAE Fundamentals (Q=Cv·A·V) · BS 5925 (unilateral) · verifique as normas locais',
                'it-IT': 'Modello educativo — ASHRAE Fundamentals (Q=Cv·A·V) · BS 5925 (unilaterale) · verifica le norme locali',
                'sv-SE': 'Pedagogisk modell — ASHRAE Fundamentals (Q=Cv·A·V) · BS 5925 (ensidig) · BFS 2024:8 för svenska krav'
            })
        };
    }
}

// ============================================
// INICIALIZAÇÃO
// ============================================

const app = new VentilacaoApp();
app.inicializar();
